import { useEffect, useRef } from "react";
import { FaEuroSign } from "react-icons/fa6";
import { HiOutlineInformationCircle } from "react-icons/hi2";
import { Checkbox, Input } from "rizzui";
import { toast } from "sonner";
import CustomSelect from "../../../components/forms/CustomSelect";
import RequiredInput from "../../../components/forms/InputHOC";
import useProductComplements from "../../../hooks/api-calls/useProductComplements";
import useProductTypes from "../../../hooks/api-calls/useProductTypes";
import { useFindProducts } from "../../../hooks/api-calls/useProducts";
import FormGroup from "../../../layout/shared/form-group";
import cn from "../../../utils/classNames";
import { usePrivilege } from "../../../components/priviledge/PriviledgeProvider";

interface ProductContractFormProps {
    formik: any;
}

const ProductContractForm = ({ formik }: ProductContractFormProps) => {

    const { getProductTypesList } = useProductTypes({ active: true, apply_contract_product_type_scope: true });
    const { getFoundProductsList, findProducts } = useFindProducts({ product_type: [formik.values.productTypeId], active: true });
    const { getProductComplementsList, fetchProductComplements } = useProductComplements();
    const { userCan } = usePrivilege();
    const rangeWarningKeyRef = useRef<string | null>(null);

    const canAdminComplements = userCan('admin_complements', 'complements');
    const productTypesList = getProductTypesList() || [];
    const productsList = getFoundProductsList() || [];

    const parseBooleanValue = (value: any): boolean => [true, 1, '1', 'true', 't'].includes(value);

    const showRangeWarningOnce = (warningKey: string, message: string) => {
        if (rangeWarningKeyRef.current === warningKey) return;
        rangeWarningKeyRef.current = warningKey;
        toast.warning(message);
    };

    const resetRangeWarning = () => {
        rangeWarningKeyRef.current = null;
    };

    const getSelectedProductType = (productTypeId = formik.values.productTypeId) => {
        return productTypesList.find((productType: any) => productType.value === productTypeId);
    };

    const resolveConsultancyFieldsRequired = (product: any, productTypeId = formik.values.productTypeId) => {
        const productRequiresConsultancy = parseBooleanValue(product?.consultancyFieldsRequired ?? false);
        const productTypeRequiresConsultancy = parseBooleanValue(
            product?.productTypeConsultancyFieldsRequired ?? getSelectedProductType(productTypeId)?.consultancyFieldsRequired ?? false
        );

        return productRequiresConsultancy || productTypeRequiresConsultancy;
    };

    const findRangeAmountByWorkers = (
        workersRangePrices: any[],
        workersNumber: number,
        workersRangeExtraPriceEnabled: boolean,
        workersRangeExtraPricePerWorker: number | null
    ): number | null => {
        if (!workersRangePrices?.length) return null;

        const rangeFound = workersRangePrices.find((range: any) => {
            const workersFrom = Number(range.workersFrom ?? range.workers_from);
            const workersTo = Number(range.workersTo ?? range.workers_to);
            return workersNumber >= workersFrom && workersNumber <= workersTo;
        });

        if (rangeFound) return Number(rangeFound.amount ?? 0);

        if (!workersRangeExtraPriceEnabled || !Number.isFinite(workersRangeExtraPricePerWorker) || Number(workersRangeExtraPricePerWorker) <= 0) {
            return null;
        }

        const sortedRanges = [...workersRangePrices].sort((currentRange: any, nextRange: any) => {
            const currentWorkersTo = Number(currentRange.workersTo ?? currentRange.workers_to ?? 0);
            const nextWorkersTo = Number(nextRange.workersTo ?? nextRange.workers_to ?? 0);
            return currentWorkersTo - nextWorkersTo;
        });

        const maxRange = sortedRanges[sortedRanges.length - 1];

        if (!maxRange) return null;

        const maxWorkersTo = Number(maxRange.workersTo ?? maxRange.workers_to ?? 0);
        const maxRangeAmount = Number(maxRange.amount ?? 0);

        if (workersNumber <= maxWorkersTo) return null;

        const extraWorkers = workersNumber - maxWorkersTo;
        const calculatedAmount = maxRangeAmount + (extraWorkers * Number(workersRangeExtraPricePerWorker));

        return Number(calculatedAmount.toFixed(2));
    };

    /**
     * Fetch products when product type is selected
     * If product type is not selected, reset product and product complements
     */
    useEffect(() => {
        if (formik.values.productTypeId) {
            findProducts({ product_type: [formik.values.productTypeId] });
        } else {
            formik.setFieldValue('productId', '');
            formik.setFieldValue('productAmount', 0);
            formik.setFieldValue('totalAmount', 0);
            formik.setFieldValue('complements', []);
            formik.setFieldValue('studentFieldsRequired', false);
            formik.setFieldValue('consultancyFieldsRequired', false);
            resetRangeWarning();
        }
    }, [formik.values.productTypeId]);

    /**
     * Fetch product complements when product is selected
     * Set default values for product amount
     * Set boolean flags for student and consultancy fields based on product data
     */
    useEffect(() => {
        if (formik.values.productId) {
            fetchProductComplements(formik.values.productId);

            if (productsList.length) {
                const selectedProduct = productsList.find((g: any) => g.value === formik.values.productId);

                if (!selectedProduct) return;

                formik.setFieldValue('studentFieldsRequired', selectedProduct?.studentFieldsRequired ?? false);
                formik.setFieldValue('consultancyFieldsRequired', resolveConsultancyFieldsRequired(selectedProduct));

                const workersRangePriceEnabled = selectedProduct?.workersRangePriceEnabled ?? false;
                const workersRangeExtraPriceEnabled = parseBooleanValue(
                    selectedProduct?.workersRangeExtraPriceEnabled ?? selectedProduct?.workers_range_extra_price_enabled ?? false
                );
                const workersRangeExtraPricePerWorkerRaw = selectedProduct?.workersRangeExtraPricePerWorker ?? selectedProduct?.workers_range_extra_price_per_worker ?? null;
                const workersRangeExtraPricePerWorker = workersRangeExtraPricePerWorkerRaw !== null && workersRangeExtraPricePerWorkerRaw !== undefined
                    ? Number(workersRangeExtraPricePerWorkerRaw)
                    : null;
                const workersNumber = Number(formik.values.clientWorkersNumber);
                const hasValidWorkersNumber = Number.isInteger(workersNumber) && workersNumber > 0;

                if (!workersRangePriceEnabled) {
                    resetRangeWarning();
                    if (formik.values.productAmount === 0) {
                        formik.setFieldValue('productAmount', selectedProduct?.amount ?? 0);
                    }
                    return;
                }

                if (!formik.values.clientId || !hasValidWorkersNumber) {
                    showRangeWarningOnce(
                        `missing-client-${selectedProduct.value}`,
                        'El producto seleccionado tiene precio por rango de trabajadores y todavía no se ha seleccionado cliente o número de trabajadores.'
                    );

                    if (formik.values.productAmount === 0) {
                        formik.setFieldValue('productAmount', selectedProduct?.amount ?? 0);
                    }
                    return;
                }

                const rangeAmount = findRangeAmountByWorkers(
                    selectedProduct.workersRangePrices,
                    workersNumber,
                    workersRangeExtraPriceEnabled,
                    workersRangeExtraPricePerWorker
                );

                if (rangeAmount === null) {
                    showRangeWarningOnce(
                        `missing-range-${selectedProduct.value}-${workersNumber}`,
                        `No existe un rango configurado para ${workersNumber} trabajadores ni precio extra por trabajador a partir del máximo en este producto.`
                    );
                    return;
                }

                resetRangeWarning();

                if (Number(formik.values.productAmount) !== Number(rangeAmount)) {
                    formik.setFieldValue('productAmount', rangeAmount);
                }
            }
        } else {
            resetRangeWarning();
        }
    }, [formik.values.productId, formik.values.clientId, formik.values.clientWorkersNumber, productsList]);

    /**
     * Set default values for complements and total amount
     */
    useEffect(() => {
        if (formik.values.productAmount && formik.values.productAmount !== 0) {
            formik.setFieldValue('totalAmount', parseFloat((Number(formik.values.productAmount)).toFixed(2)));
        }
    }, [formik.values.productAmount]);

    /**
     * Update total amount when complements are updated
     */
    useEffect(() => {
        if (formik.values.complements.length) {
            const totalAmount = formik.values.complements.reduce((acc: number, complement: any) => acc + complement.amount, Number(formik.values.productAmount));
            formik.setFieldValue('totalAmount', parseFloat(totalAmount.toFixed(2)));
        } else {
            formik.setFieldValue('totalAmount', formik.values.productAmount);
        }
    }, [formik.values.complements]);

    return (
        <div className="rounded-md border-solid border-2 col-span-7 p-6 divide-y-2 divide-dashed divide-gray-200 gap-7">
            <div className='flex flex-col md:flex-row md:justify-between items-center'>
                <h3 className='font-bold text-lg pb-2'>Datos del Producto</h3>
            </div>

            <FormGroup
                title="Información General"
                description="Producto asociado al contrato."
                className={cn("py-4 @3xl:grid-cols-12 ")}
                titleCols="@md:col-span-2"
                childCols="@md:col-span-10 md:grid-cols-4"
            >
                <div className="grid grid-cols-12 col-span-8 gap-4">
                    <div className="col-span-12 md:col-span-3 space-y-2">
                        <CustomSelect
                            isSearchable
                            isClearable
                            required
                            id={'productTypeId'}
                            label="Tipo de producto"
                            containerClassName=""
                            value={formik.values.productTypeId ? { value: formik.values.productTypeId, label: productTypesList.find((g: any) => g.value === formik.values.productTypeId)?.label } : null}
                            options={productTypesList}
                            onChange={(e: any) => {
                                formik.setFieldValue('productTypeId', e?.value ?? '');
                                formik.setFieldValue('productId', '');
                                formik.setFieldValue('productAmount', 0);
                                formik.setFieldValue('totalAmount', 0);
                                formik.setFieldValue('complements', []);
                                formik.setFieldValue('studentFieldsRequired', false);
                                formik.setFieldValue('consultancyFieldsRequired', parseBooleanValue(e?.consultancyFieldsRequired ?? false));
                            }}
                            isDisabled={!userCan('get_product_types_for_select', 'product_types')}
                            error={(formik.dirty && formik.errors.productTypeId) ? formik.errors.productTypeId : undefined}
                        />
                        {!userCan('get_product_types_for_select', 'product_types') && (
                            <div className="flex items-center gap-2 text-red-500 text-sm">
                                <HiOutlineInformationCircle size={18} className="flex-shrink-0" />
                                <span>No tienes permisos para ver los tipos de producto</span>
                            </div>
                        )}
                    </div>

                    <CustomSelect
                        isSearchable
                        isClearable
                        required={formik.values.saveAsContract}
                        id={'productId'}
                        label="Producto"
                        containerClassName="col-span-12 md:col-span-5"
                        value={formik.values.productId ? { value: formik.values.productId, label: getFoundProductsList()?.find((g: any) => g.value === formik.values.productId)?.label } : null}
                        options={productsList}
                        onChange={(e: any) => {
                            formik.setFieldValue('productId', e?.value ?? '');
                            formik.setFieldValue('productAmount', e?.amount ?? 0);
                            formik.setFieldValue('complements', []);
                            formik.setFieldValue('studentFieldsRequired', e?.studentFieldsRequired ?? false);
                            formik.setFieldValue('consultancyFieldsRequired', resolveConsultancyFieldsRequired(e));
                        }}
                        error={formik.errors.productId}
                        isDisabled={!formik.values.productTypeId || !productsList?.length}
                    />

                    <RequiredInput
                        label="Importe"
                        id="productAmount"
                        type="number"
                        className={cn("col-span-12 md:col-span-2", !formik.values.productId && 'hidden')}
                        suffix={<FaEuroSign />}
                        formik={formik}
                    />

                    {(() => {
                        const productType = getProductTypesList()?.find((g: any) => g.value === formik.values.productTypeId) ?? null;
                        const isSubsidisedCourse = productType?.value === '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1' || productType?.label === 'Bonificada';
                        const selectedProduct = productsList?.find((g: any) => g.value === formik.values.productId);
                        const fundaeCode = selectedProduct?.code;

                        return isSubsidisedCourse && formik.values.productId ? (
                            <Input
                                readOnly
                                label="Código de Acción Formativa"
                                id="fundaeCode"
                                className="col-span-12 md:col-span-3"
                                value={fundaeCode ?? ''}
                                placeholder="Código del producto en FUNDAE"
                            />
                        ) : null;
                    })()}
                </div>
            </FormGroup>

            {canAdminComplements && (
                <FormGroup
                    title="Complementos"
                    description="Complementos del producto."
                    className={cn("py-4", (getProductComplementsList().length === 0 || !formik.values.productId) && 'hidden')}
                    titleCols="@md:col-span-2"
                    childCols="@md:col-span-10 md:grid-cols-2"
                >
                    {getProductComplementsList()?.map((complement: any) => (
                        <div key={complement.value} className="col-span-4 md:col-span-1 flex flex-col">
                            <Checkbox
                                size='md'
                                label={<span className="text-wrap">{complement.label} <span className="text-gray-400">({complement.amount}€)</span></span>}
                                checked={formik.values.complements.find((c: any) => c.complementId === complement.value)}
                                onChange={(e: any) => {
                                    if (e && !formik.values.complements.find((c: any) => c.complementId === complement.value)) {
                                        formik.setFieldValue('complements', [...formik.values.complements, { complementId: complement.value, amount: complement.amount }]);
                                        formik.setFieldValue('totalAmount', parseFloat((Number(formik.values.totalAmount) + complement.amount).toFixed(2)));
                                    } else {
                                        const complementIndex = formik.values.complements.findIndex((c: any) => c.complementId === complement.value);
                                        if (complementIndex !== -1) {
                                            formik.setFieldValue('complements', formik.values.complements.filter((c: any, i: number) => i !== complementIndex));
                                            formik.setFieldValue('totalAmount', parseFloat((Number(formik.values.totalAmount) - complement.amount).toFixed(2)));
                                        }
                                    }
                                }}
                                labelClassName="pl-2 text-sm font-medium !text-gray-900"
                            />

                            <Input
                                id={`complement_${complement.value}`}
                                label="Nuevo Importe"
                                type="number"
                                suffix={<FaEuroSign />}
                                value={formik.values.complements.find((c: any) => c.complementId === complement.value)?.amount}
                                onChange={(e: any) => {
                                    if (e) {
                                        const complementIndex = formik.values.complements.findIndex((c: any) => c.complementId === complement.value);
                                        if (complementIndex !== -1) {
                                            formik.setFieldValue(`complements[${complementIndex}].amount`, Number(e.target.value));
                                        }
                                    }
                                }}
                                className={cn("md:w-48 mt-3", !formik.values.complements.find((c: any) => c.complementId === complement.value) && 'hidden')}
                                required={false}
                            />
                        </div>
                    ))}
                </FormGroup>
            )}

            <div className='flex flex-col md:flex-row md:justify-end items-center'>
                <span className={cn("text-md font-medium mt-3", !formik.values.productId && 'hidden')}>
                    Total: {formik.values.totalAmount || formik.values.productAmount}€
                </span>
            </div>
        </div>
    );
}

export default ProductContractForm;
